import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import SettingsCentralMappingTablesCellsEditableNumberCell from 'lumen/components/settings/central-mapping/tables/cells/editable-number-cell';
import type { CentralMappingMappingEntryRoomType } from 'lumen/models';
import type { NumericValueKeysTypes } from 'lumen/utils/central-mapping-constants';

interface MarketSettingsCentralMappingTablesCellsTdNumberSignature {
  Args: {
    cellData: number;
    column: {
      isReadOnly?: boolean;
      maxValue: number;
      placeholder: string;
      tdNumberDataTestId: string;
      valueKey: NumericValueKeysTypes;
    };
    row: {
      isMixedNumericCells?: {
        key: NumericValueKeysTypes;
        value: boolean;
      }[];
      isParent?: boolean;
      mappingEntry: CentralMappingMappingEntryRoomType;
    } & {
      [key in NumericValueKeysTypes]: number | null;
    };
    rowMeta: {
      isSelected: boolean;
    };
  };
}

export default class MarketSettingsCentralMappingTablesCellsTdNumber extends Component<MarketSettingsCentralMappingTablesCellsTdNumberSignature> {
  @service declare centralMapping: Services['central-mapping'];

  get isMixed(): boolean {
    if (!this.args.row.isParent) {
      return false;
    }

    return (
      this.args.row.isMixedNumericCells?.some(
        (mixed) => mixed.key === this.args.column.valueKey && mixed.value,
      ) ?? false
    );
  }

  get isReadOnly(): boolean {
    return (
      this.args.column.isReadOnly ||
      Boolean(this.args.rowMeta.isSelected) ||
      Boolean(this.args.row.isParent)
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get value() {
    const { row, column } = this.args;

    return row[column.valueKey];
  }

  @action setValue(value: number | null): void {
    const { row, column } = this.args;
    const { mappingEntry } = row;
    mappingEntry[column.valueKey] = value;

    const isParentRow = Boolean(this.args.row.isParent);
    this.centralMapping.trackMappingEntryChanged({
      mappingChange: column.valueKey,
      rowType: isParentRow ? 'parent' : 'child',
    });
  }

  <template>
    <SettingsCentralMappingTablesCellsEditableNumberCell
      @cellDataTestId={{@column.tdNumberDataTestId}}
      @isMixed={{this.isMixed}}
      @isReadOnly={{this.isReadOnly}}
      @maxValue={{@column.maxValue}}
      @placeholder={{@column.placeholder}}
      @setValue={{this.setValue}}
      @value={{this.value}}
    />
  </template>
}
